/**
 * sessionRecovery.js — Auto-réparation de session côté client.
 *
 * Objectif : un client ne doit JAMAIS rester bloqué (401 « Session expirée »,
 * modal pays/nom, « erreur survenue » à la commande) parce qu'un cookie gs_cid,
 * un deviceId ou un pwaToken manque / est périmé.
 *
 * Stratégies tentées dans l'ordre (la 1re qui réussit gagne, le serveur repose
 * alors le cookie gs_cid) :
 *   1. pwaToken stocké          → GET /pwa/resolve-token
 *   2. cookie signet (serveur)  → GET /device/whoami
 *   3. deviceId déjà lié        → GET /device/resolve
 *   4. licence appareil ECDSA   → challenge / verify (chatId connu du storage)
 *
 * Retourne le chatId prouvé ou null. Ne lève jamais d'exception.
 */

import { hasStoredLicenseKey, verifyAndAuthenticate } from './licenseAuth';

const DEVICE_ID_KEY = 'telegramShopDeviceId';

const API_BASE = (() => {
  try {
    const fromEnv = String(
      (typeof process !== 'undefined' && (process.env?.REACT_APP_API_URL || process.env?.REACT_APP_BACKEND_URL)) || ''
    ).replace(/\/$/, '');
    if (fromEnv) return fromEnv.endsWith('/api') ? fromEnv : `${fromEnv}/api`;
  } catch { /* */ }
  return '/api';
})();

const lsGet = (k) => {
  try { return window.localStorage.getItem(k) || ''; } catch { return ''; }
};

const getDeviceId = () => {
  let id = lsGet(DEVICE_ID_KEY);
  if (!id) {
    try { id = window.sessionStorage.getItem(DEVICE_ID_KEY) || ''; } catch { id = ''; }
  }
  return id && id.length >= 8 ? id : '';
};

/** Génère (et persiste) un deviceId si aucun n'existe — évite les liens signet impossibles. */
export const ensureDeviceIdSafe = () => {
  let id = getDeviceId();
  if (id) return id;
  try {
    id = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `dev_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    window.localStorage.setItem(DEVICE_ID_KEY, id);
  } catch { /* */ }
  return id;
};

const getJson = async (url, timeoutMs = 6000) => {
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;
  try {
    const res = await fetch(url, { credentials: 'include', signal: ctrl?.signal });
    const ct = res.headers.get('content-type') || '';
    const data = ct.includes('application/json') ? await res.json().catch(() => null) : null;
    return { status: res.status, data };
  } catch {
    return { status: 0, data: null };
  } finally {
    if (timer) clearTimeout(timer);
  }
};

let inflight = null;

/**
 * Tente de rétablir la session serveur. Appels concurrents mutualisés.
 * @returns {Promise<string|null>} chatId prouvé ou null
 */
export function recoverApiSession() {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      // Compte révoqué explicitement → ne pas insister (géré par les modals existants)
      const isRevoked = (r) => r?.data?.accountRevoked || r?.data?.accountDisabled;

      // Court-circuit post-resetall : si tous les tokens ont été supprimés par le modal
      // "compte révoqué", il n'y a rien à récupérer → retourner null immédiatement.
      const pwaCheck = lsGet('pwaToken');
      const chatCheck = lsGet('telegramChatId') || lsGet('chatId');
      const deviceCheck = getDeviceId();
      if (!pwaCheck && !chatCheck && !deviceCheck) {
        return null;
      }

      // 1. pwaToken
      const pwa = pwaCheck;
      if (pwa) {
        const r = await getJson(`${API_BASE}/pwa/resolve-token?t=${encodeURIComponent(pwa)}`);
        if (isRevoked(r)) return null;
        if (r.data?.success && r.data.chatId) return String(r.data.chatId);
      }

      // 2. cookie signet lu côté serveur
      const did = ensureDeviceIdSafe();
      const w = await getJson(`${API_BASE}/device/whoami?d=${encodeURIComponent(did)}`);
      if (w.data?.success && w.data.chatId) return String(w.data.chatId);

      // 3. deviceId déjà lié
      if (did) {
        const d = await getJson(`${API_BASE}/device/resolve?d=${encodeURIComponent(did)}`);
        if (isRevoked(d)) return null;
        if (d.data?.success && d.data.chatId) return String(d.data.chatId);
      }

      // 4. licence appareil (preuve par clé privée)
      const storedChat = lsGet('telegramChatId') || lsGet('chatId');
      if (storedChat && hasStoredLicenseKey()) {
        const v = await verifyAndAuthenticate(storedChat);
        if (v?.success && v.chatId) return String(v.chatId);
      }
      return null;
    } catch {
      return null;
    }
  })().finally(() => { inflight = null; });
  return inflight;
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * POST JSON résilient (commande) :
 *  - 401           → recoverApiSession() puis 1 nouvel essai
 *  - réseau/timeout/HTML/502-504 → 1 nouvel essai après 1,5 s
 *    (le serveur répond 409 si la 1re tentative était passée → déjà géré par l'appelant)
 *
 * @param {() => Promise<Response>} doFetch  fonction qui refait l'appel (headers frais à chaque fois)
 * @returns {Promise<{response: Response|null, data: any, error: Error|null}>}
 */
export async function resilientJsonRequest(doFetch) {
  const attempt = async () => {
    try {
      const response = await doFetch();
      const ct = response.headers.get('content-type') || '';
      let data = null;
      if (ct.includes('application/json')) {
        try { data = await response.json(); } catch { data = null; }
      }
      return { response, data, error: null };
    } catch (error) {
      return { response: null, data: null, error };
    }
  };

  let out = await attempt();

  if (out.response?.status === 401) {
    const cid = await recoverApiSession();
    if (cid) out = await attempt();
    return out;
  }

  const status = out.response?.status || 0;
  const transient = !!out.error || (!out.data && (status === 0 || status >= 500)) || [502, 503, 504].includes(status);
  if (transient) {
    await sleep(1500);
    const retry = await attempt();
    // Si le retry tombe en 401, tenter la récupération une fois
    if (retry.response?.status === 401) {
      const cid = await recoverApiSession();
      if (cid) return attempt();
    }
    return retry;
  }
  return out;
}